import { Dates, WorkerSummaryReportData, Translations, Language, SummaryCalculationRow, IncomeTypesLogic, EnumerationsLogic, CalculationRowType } from "@salaxy/core";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";
import { WorkerSummaryReportDataExtended } from "./WorkerSummaryReportDataExtended";

/**
 * Mapper for worker summary report
 */
export class WorkerSummaryMapper {
  /**
   * Creates HeaderFooterData from provided parameters
   * @param businessObject WorkerSummaryReportData used to get neede data for header
   * @param options Report options
   * @param lang gets translation with this lang, if null use current lang
   * @returns HeaderFooterData for provided businessObject
   */
   public static getWorkerSummaryReportHeaderFooter(businessObject: WorkerSummaryReportData, options: ReportOptions, lang: Language = null): HeaderFooterData {
    const headerFooter: HeaderFooterData = {
      logo: options?.logo,
      img2: options?.img2,
      title: `${Translations.get("SALAXY.REPORTS.workerSummaryReport.title", null, lang)} ${Dates.getYear(businessObject.startDate)}`,
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(Dates.getToday())
    };

    return headerFooter;
  }

  /**
   * Creates ReporLayout object depending on switchToPortrait boolean
   * @param switchToPortrait boolean indicating if layout should be portrait
   * @param pdf mode for report
   * @returns ReportLayout object
   */
   public static getReportLayout(switchToPortrait: boolean, pdf = false): ReportLayout {
    if (switchToPortrait) {
      return {
        isLayoutA4Portrait: true,
        isLayoutA4Landscape: false,
        layout: "a4Portrait",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    } else {
      return {
        isLayoutA4Portrait: false,
        isLayoutA4Landscape: true,
        layout: "a4Landscape",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    }
  }

  /**
   * Maps data to hbs supported report format
   * @param businessObject businessObject used to create WorkerSummaryReportDataExtended
   * @returns WorkerSummaryReportData with 4 added boolean fields as WorkerSummaryReportDataExtended
   */
  public static getWorkerSummaryReport(businessObject: WorkerSummaryReportData, lang: Language = null): WorkerSummaryReportDataExtended {
    [...businessObject.workerResult.cashSalary.rows, ...businessObject.workerResult.benefitsInKind.rows, ...businessObject.workerResult.expenses.rows].forEach(item => {
      item.message = this.translateMessage(item, lang);
    });
    const data = businessObject as WorkerSummaryReportDataExtended;
    data.showUnionPayment = businessObject.result.workerCalc.unionPayment > 0 ? true : false;
    data.showForeclosure = businessObject.result.workerCalc.foreclosure > 0 ? true : false;
    data.showPrepaidExpenses = businessObject.result.workerCalc.prepaidExpenses > 0 ? true : false;
    data.showOtherDeductions = businessObject.result.workerCalc.otherDeductions > 0 ? true : false;
    return data;
  }

  private static translateMessage(data: SummaryCalculationRow, lang: Language): string {
    const item = data as any;
    if(!item.data) {
      return item.message;
    }
    let message = "";
    let rowType = item.data.calculationRowType || CalculationRowType.Unknown;
    rowType = (rowType.substr(0, 1).toLowerCase() + rowType.substr(1)) as CalculationRowType;
    const code = item.data.code as number;
    const kind = item.data.kind as string;
    if (rowType === CalculationRowType.IrIncomeType) {
      message = IncomeTypesLogic.getAll(lang).find( (x) => (kind && x.transactionCode === kind) || (code > 0 && x.code === code))?.label;
    } else if (rowType === CalculationRowType.MealBenefit && code !== 334) {
      // Meal benefit deductions
      message = IncomeTypesLogic.getAll(lang).find( (x) => code > 0 && x.code === code)?.label;
    } else if([
      CalculationRowType.Unknown,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.TaxAtSource,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation
    ].includes(rowType)) {
      message = IncomeTypesLogic.getAll(lang).find( (x) => code > 0 && x.code === code)?.label;
    } else if([
      CalculationRowType.DailyAllowance,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.MealBenefit, // main MealBenefit
      CalculationRowType.Training,
      CalculationRowType.PaidSickLeave
    ].includes(rowType) && kind) {
        message =  EnumerationsLogic.getEnumLabel(`${item.data.calculationRowType}Kind`, kind, null, lang);
    }
    return message || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, lang);
  }
}